<script setup>
import DataProvider from '@/components/DataProvider';
import { ref, watch, nextTick, useAttrs, computed } from 'vue';

const props = defineProps({
    modelValue: Object,
    valueType: {
        type: String,
        default: 'Practitioner',
    },
    filter: Function,
    autoClear: Boolean,
    label: String,
    minLength: {
        type: Number,
        default: 0,
    },
    clearable: {
        type: Boolean,
        default: true,
    },
    providerOnly: {
        type: Boolean,
        default: false,
    },
});
const emit = defineEmits(['update:modelValue', 'itemClick', 'valueCleared', 'blur']);
const attrs = useAttrs();
const autocompleteValue = ref(null);
const autocompleteRef = ref(null);
const search = ref(null);
const users = ref([]);

watch(
    () => props.modelValue,
    (newValue) => {
        if (newValue) {
            nextTick(() => {
                const items = filteredUsers.value;
                let found = null;
                if (newValue.resourceType === 'User') {
                    found = items.find((item) => item.id === newValue.id) || null;
                } else if (newValue.resourceType === 'Practitioner') {
                    found = items.find((item) => item.practitioner?.id === newValue.id) || null;
                }
                if (found) {
                    autocompleteValue.value = found;
                }
            });
        } else {
            autocompleteValue.value = null;
        }
    },
    { immediate: true, deep: true },
);

// Methods
const loadedData = () => {
    if (props.modelValue) {
        const items = filteredUsers.value;
        if (props.modelValue.resourceType === 'User') {
            autocompleteValue.value = items.find((item) => item.id === props.modelValue.id) || null;
        } else if (props.modelValue.resourceType === 'Practitioner') {
            autocompleteValue.value = items.find((item) => item.practitioner?.id === props.modelValue.id) || null;
        }
    }
    if (autocompleteRef.value && attrs.autofocus) {
        autocompleteRef.value.focus();
    }
};
const itemClick = (item) => {
    if (!item) {
        emit('valueCleared');
        return;
    }

    autocompleteValue.value = item;

    let itemVal;
    if (props.valueType === 'Practitioner') {
        itemVal = {
            id: item.practitionerResource?.id,
            resourceType: 'Practitioner',
            fullName: item.practitionerResource?.fullName,
            display: item.practitionerResource?.fullName,
        };
    } else {
        itemVal = item;
    }

    emit('update:modelValue', itemVal);
    emit('itemClick', item);

    nextTick(() => {
        if (props.autoClear) {
            autocompleteValue.value = null;
            search.value = null;
        }
    });
};
const mappedUsers = (users) => {
    if (props.minLength > 0 && (!search.value || search.value.length <= props.minLength)) {
        return [];
    }

    let mappedUsers = users
        .filter((i) => i.resourceType === 'User' && i.practitioner)
        .flatMap((user) => {
            const practitioner = users.find((a) => a.resourceType === 'Practitioner' && user.practitioner.id === a.id);
            if (!practitioner) return [];
            user.practitionerResource = practitioner;
            return user;
        });

    // Only return active users
    mappedUsers = mappedUsers.filter((user) => user.active);

    if (props.filter) {
        mappedUsers = mappedUsers.filter(props.filter);
    }

    return mappedUsers;
};
const userFilter = (firstName, search, item) => {
    let user = item.raw;
    let filterOne, filterTwo, filterThree;

    if (user.practitionerResource.firstName) {
        filterOne = user.practitionerResource.firstName.toLowerCase();
        filterTwo = user.practitionerResource.lastName.toLowerCase();
        filterThree = user.practitionerResource.fullName.toLowerCase();
    }
    const filter = firstName.toLowerCase();
    const searchText = search.toLowerCase();

    return filterOne.indexOf(searchText) > -1 || filterTwo.indexOf(searchText) > -1 || filterThree.indexOf(searchText) > -1 || filter.indexOf(searchText) > -1;
};

const filteredUsers = computed(() => {
    let result = users.value ? mappedUsers(users.value) : [];

    // Apply text search filter
    if (search.value) {
        const searchTerms = search.value.toLowerCase().split(' ');
        result = result.filter((user) => {
            // Guard against null user
            if (!user) return false;

            // Safely access properties with optional chaining
            const firstName = user.practitionerResource?.firstName?.toLowerCase() || '';
            const lastName = user.practitionerResource?.lastName?.toLowerCase() || '';
            const fullName = `${firstName} ${lastName}`.trim();

            // Fix the email access - it might not exist or might not be a string
            let email = '';
            if (typeof user.practitionerResource?.email === 'string') {
                email = user.practitionerResource.email.toLowerCase();
            } else if (user.email && typeof user.email === 'string') {
                email = user.email.toLowerCase();
            }

            return searchTerms.every((term) => firstName.includes(term) || lastName.includes(term) || fullName.includes(term) || email.includes(term));
        });
    }

    // Filter to only include providers if providerOnly is true
    if (props.providerOnly) {
        result = result.filter((user) => {
            // Guard against null user
            if (!user) return false;
            return user.isProvider === true;
        });
    }

    return result;
});
</script>

<template>
    <DataProvider v-model="users" collection :query="{ query: { _include: 'practitioner' }, resourceType: 'User' }" @loaded="loadedData">
        <template #loading>
            <v-autocomplete :disabled="true" v-bind="attrs" :label="props.label || 'Search user'" />
        </template>
        <template #error> Error </template>
        <template #default>
            <v-autocomplete
                ref="autocompleteRef"
                v-model:search="search"
                :model-value="autocompleteValue"
                return-object
                :clearable="props.clearable"
                hide-selected
                :custom-filter="userFilter"
                :items="filteredUsers"
                item-title="practitionerResource.fullName"
                :label="props.label || 'Search user'"
                v-bind="attrs"
                persistent
                @update:model-value="itemClick"
                @blur="$emit('blur')"
            >
                <template #no-data>
                    <v-list-item>
                        <v-list-item-title v-if="props.minLength <= 0"> Users not found </v-list-item-title>
                        <v-list-item-title v-else> Start typing to search for users </v-list-item-title>
                    </v-list-item>
                </template>
                <template #item="{ item, props: itemProps }">
                    <v-list-item v-bind="itemProps" :title="null">
                        <v-list-item-title class="text-truncate" data-cy="userSearchListItem">
                            {{
                                item.raw.practitionerResource?.fullName ||
                                (item.raw.practitionerResource?.firstName && item.raw.practitionerResource?.lastName
                                    ? `${item.raw.practitionerResource.firstName} ${item.raw.practitionerResource.lastName}`
                                    : 'Unknown Provider')
                            }}
                        </v-list-item-title>
                    </v-list-item>
                </template>
            </v-autocomplete>
        </template>
    </DataProvider>
</template>
